import React, { useState, useEffect, useRef } from "react";
import { Link, Redirect, useLocation } from "react-router-dom";
import Header from "../components/Header";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, updateUserData, getPushToken, setOnBoardingData } from "../actions/user";
import { matchPath } from "react-router";
import { useTranslation } from "react-i18next";
import { getSiteName, setSignupFormValues } from "../actions/config";
import { useForm } from "react-hook-form";
import isEmail from "validator/lib/isEmail";
import { useSignUp, useHello } from "../graphql/useUser";
import Loading from "../components/Loading";
import ErrorPage from "../components/ErrorPage";
import { toast } from "react-toastify";
import swal from "@sweetalert/with-react";
import { FirebaseGetNewToken } from "../firebase";
import { setPushToken } from "../actions/notifications";
import { useConfiguredTerms, useConfiguredPrivacy, useAskMarketing } from "../utils/config";
import { resetOnBoardingUI } from "../actions/user";
import { useUsageStats } from "../hooks/useUsageStats";

import useOnBoardingData from "hooks/useOnBoardingData";

const collectApolloErrorMessages = (error) => {
    if (!error) {
        return [];
    }

    const messages = [];
    if (error.message) {
        messages.push(error.message);
    }

    if (Array.isArray(error.graphQLErrors)) {
        error.graphQLErrors.forEach(({ message }) => {
            if (message) {
                messages.push(message);
            }
        });
    }

    const networkErrors = error?.networkError?.result?.errors;
    if (Array.isArray(networkErrors)) {
        networkErrors.forEach(({ message }) => {
            if (message) {
                messages.push(message);
            }
        });
    }

    return messages;
};

const isExistingAccountSignupError = (error) => {
    const patterns = [
        /data conflict error/i,
        /customer onboarding already complete/i,
        /onboarding already complete/i,
        /already registered/i,
        /already exists/i,
    ];
    const messages = collectApolloErrorMessages(error);
    return messages.some((message) => patterns.some((pattern) => pattern.test(message)));
};

const SignUp = () => {
    const { t, i18n } = useTranslation();
    const i18nLang = (i18n.language || "").match(/^[^-]+/)[0];
    const storageLangContent = localStorage.getItem("storage_lang_content") || i18nLang;
    const location = useLocation();
    const dispatch = useDispatch();
    const { hello } = useHello();
    const pushToken = useSelector(getPushToken);
    const lastSignupFormValues = useSelector((state) => state.session.lastSignupFormValues);
    const stateRedirect = useRef(null);

    const { obData, hasEmailVerified } = useOnBoardingData();
    const [passVisible, setPassVisible] = useState(false);
    const [redirectTo, setRedirectTo] = useState("");
    const [lastPass, setLastPass] = useState("");

    const didPrefillFromOnboarding = useRef(false);

    const terms = useConfiguredTerms(storageLangContent);
    const privacy = useConfiguredPrivacy(storageLangContent);
    const askMarketing = useAskMarketing();

    const { trackSignUp } = useUsageStats();

    const { register, getValues, setValue, handleSubmit, watch, errors, triggerValidation } = useForm();

    useEffect(() => {
        if (!obData || didPrefillFromOnboarding.current) return;

        if (lastSignupFormValues) return;

        const { email, name, surname } = obData || {};
        const current = getValues();

        if (email && !current.email) {
            setValue("email", email, { shouldValidate: true, shouldDirty: false });
        }
        if (hasEmailVerified) {
            if (name && !current.name) {
                setValue("name", name, { shouldValidate: true, shouldDirty: false });
            }
            if (surname && !current.surname) {
                setValue("surname", surname, { shouldValidate: true, shouldDirty: false });
            }
        }
        didPrefillFromOnboarding.current = true;
    }, [obData, getValues, setValue, lastSignupFormValues, hasEmailVerified]);

    const signupError = (alreadyExists) => {
        var title, text;
        if (alreadyExists) {
            title = t("error.signupUserExistsTitle", { defaultValue: t("error.signupExistsTitle") });
            text = t("error.signupUserExists", { defaultValue: t("error.signupExists") });
        } else {
            title = t("error.signupTitle");
            text = t("error.signup") + "\n" + t("error.try again");
        }
        swal({
            title: title,
            text: text,
            button: t("alert ok"),
        });
    };

    const normalizeEmail = (v) => (v || "").trim().toLowerCase().normalize("NFKC");

    const { signUp, loading } = useSignUp({
        onCompleted: (data) => {
            const user = data.signUp.user;
            const token = data.signUp.auth.token;
            if (user && token) {
                FirebaseGetNewToken().then((token) => {
                    dispatch(setPushToken(token));
                    hello({ variables: { push: token } });
                });
                dispatch(loginUser(user.ref, token, lastPass));
                dispatch(updateUserData(user, "password"));
                dispatch(setOnBoardingData(null));
                dispatch(resetOnBoardingUI());
                toast(t("hello user", { name: user.name }) + "\n" + t("welcome message"));

                const emailFromResponse = normalizeEmail(user?.email);
                const onboardingEmail = normalizeEmail(obData?.email);

                if (user?.emailVerified) {
                    setRedirectTo("/site");
                } else if (hasEmailVerified && onboardingEmail && emailFromResponse === onboardingEmail) {
                    setRedirectTo("/site");
                } else {
                    setRedirectTo(redirectAfterLogin);
                }
            } else {
                signupError(false);
            }
        },
        onError: (error) => {
            const alreadyExists = isExistingAccountSignupError(error);
            if (!alreadyExists) {
                console.error(error);
            }
            signupError(alreadyExists);
        },
    });

    const locationProfile = matchPath(location.pathname, { path: "/profile" });
    const locationBooking = matchPath(location.pathname, {
        path: "/site/booking",
    });
    const locationAddRoom = matchPath(location.pathname, {
        path: "/site/addroom",
    });
    const locationZones = matchPath(location.pathname, { path: "/site/zones" });
    const locationCuiner = matchPath(location.pathname, { path: "/pos" });

    const searchParams = new URLSearchParams(window.location.search);
    const isZonesWebView = searchParams.get("zones") === "true";

    let locationSection = "login";
    if (locationAddRoom) {
        locationSection = "add_room";
    } else if (locationBooking) {
        locationSection = "my_bookings";
    } else if (locationZones) {
        locationSection = "hotel_areas";
    } else if (locationProfile) {
        locationSection = "profile";
    }
    const sectionName = locationSection;

    let redirectAfterLogin = "/verify/email";
    if (locationAddRoom) {
        locationSection = "/site/addroom/verify/email";
    } else if (locationProfile) {
        redirectAfterLogin = "/profile/verify/email";
    } else if (locationBooking) {
        redirectAfterLogin = "/site/booking/verify/email";
    } else if (locationZones) {
        redirectAfterLogin = "/site/zones/verify/email";
    } else if (locationCuiner) {
        redirectAfterLogin = "/pos/cart";
    }

    const togglePassVisibility = () => {
        setPassVisible(!passVisible);
    };

    const saveFormState = () => {
        dispatch(setSignupFormValues(getValues()));
    };

    const getSignUpOrigin = () => {
        if (isZonesWebView) {
            return "AREAS_AND_ACTIVITIES";
        }

        const originMap = {
            my_bookings: "AREAS_AND_ACTIVITIES",
            hotel_areas: "AREAS_AND_ACTIVITIES",
        };
        return originMap[locationSection] || null;
    };

    const onSubmit = (data) => {
        setLastPass(data.password);

        const finalName = data.name;
        const finalSurname = data.surname;
        const finalEmail = data.email;

        const signUpOrigin = getSignUpOrigin();

        signUp({
            variables: {
                email: finalEmail,
                name: finalName,
                surname: finalSurname,
                password: data.password,
                language: i18nLang,
                push: pushToken,
                marketingAccepted: data.marketing,
            },
        })
            .then((response) => {
                if (response?.data?.signUp) {
                    trackSignUp(signUpOrigin);
                }
            })
            .catch((error) => {
                console.error("SignUp mutation error:", error);
            });
    };

    useEffect(() => {
        if (lastSignupFormValues) {
            Object.keys(lastSignupFormValues).forEach(function (key) {
                setValue(key, lastSignupFormValues[key]);
            });
        }
        dispatch(setSignupFormValues(null));
    }, [lastSignupFormValues, setValue]);

    if (!terms || !privacy) {
        return <ErrorPage type="config-terms" />;
    }

    if (redirectTo) {
        return <Redirect push to={{ pathname: redirectTo, state: "SIGN_UP" }} />;
    }

    const labelClass = "float-left font-medium text-sm pl-1 pb-1 ";
    const txtRequired = t("required input");
    const checkPass = (exp) => {
        return (watch("password") || "").match(exp) ? "line-through" : errors.password ? " text-error" : "";
    };

    return (
        <section className={"basic has-top"}>
            <Header title={t("signup")} />
            <section>
                {loading ? (
                    <Loading />
                ) : (
                    <>
                        <p className="text-center">{t("signup intro", { site: getSiteName() })}</p>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="my-3 flex">
                                <div className="flex-1 mr-1">
                                    <div className={labelClass + (!watch("name") && "invisible")}>{t("name")}</div>
                                    <input
                                        ref={register({ required: txtRequired })}
                                        type="text"
                                        name="name"
                                        id="input-name"
                                        placeholder={t("name")}
                                        onChange={() => triggerValidation("name")}
                                        className={errors.name ? "error" : ""}
                                        autoFocus={true}
                                    />
                                    {errors.name && (
                                        <div className="text-error float-right text-sm">{errors.name.message}</div>
                                    )}
                                </div>
                                <div className="flex-1 ml-1">
                                    <div className={labelClass + (!watch("surname") && "invisible")}>
                                        {t("surname")}
                                    </div>

                                    <input
                                        ref={register({ required: txtRequired })}
                                        type="text"
                                        name="surname"
                                        id="input-surname"
                                        placeholder={t("surname")}
                                        onChange={() => triggerValidation("surname")}
                                        className={errors.surname ? "error" : ""}
                                    />
                                    {errors.surname && (
                                        <div className="text-error float-right text-sm">{errors.surname.message}</div>
                                    )}
                                </div>
                            </div>

                            <div className="my-3">
                                <div className={labelClass + (!watch("email") && "invisible")}>{t("email")}</div>
                                <input
                                    ref={register({
                                        required: txtRequired,
                                        validate: (value) => isEmail(value) || t("invalid email"),
                                    })}
                                    type="email"
                                    name="email"
                                    id="input-email"
                                    placeholder={t("email")}
                                    onChange={() => triggerValidation("email")}
                                    className={errors.email ? "error" : ""}
                                />
                                {errors.email && (
                                    <div className="text-error float-right text-sm">{errors.email.message}</div>
                                )}
                            </div>

                            <div className="mt-3 mb-1">
                                <div className={labelClass + (!watch("password") && "invisible")}>{t("password")}</div>
                                <div className="relative">
                                    <input
                                        ref={register({
                                            required: true,
                                            pattern: /^(?=.{6,50}$)(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z]).*$/gm,
                                        })}
                                        type={passVisible ? "text" : "password"}
                                        name="password"
                                        id="input-pass"
                                        placeholder={t("password")}
                                        onChange={() => triggerValidation("password")}
                                        className={errors.password ? "error" : ""}
                                    />
                                    <i
                                        className={
                                            "absolute right-0 m-3 text-neutral-contrast-50 text-xl cursor-pointer icon icon-eye-" +
                                            (passVisible ? "on" : "off")
                                        }
                                        id="toggle-pass-visibility"
                                        onClick={togglePassVisibility}
                                    ></i>
                                </div>
                            </div>
                            {errors.password ? errors.password.message : ""}
                            <p className={"text-sm whitespace-pre-line"}>
                                <span className={checkPass(/(?=.{6,50}$)/gm)}>{t("passrules.chars")}</span>
                                <br />
                                <span className={checkPass(/(?=.*[a-z])(?=.*[A-Z])/gm)}>{t("passrules.case")}</span>
                                <br />
                                <span className={checkPass(/(?=.*[0-9])/gm)}>{t("passrules.num")}</span>
                            </p>

                            <div className="text-left mt-3 text-sm">
                                <div>
                                    <input
                                        id="checkbox-terms"
                                        ref={register({ required: true })}
                                        type="checkbox"
                                        name="terms"
                                        onChange={() => triggerValidation("terms")}
                                        className={errors.name ? "error" : ""}
                                    />
                                    <label htmlFor="checkbox-terms" className="pl-2">
                                        {t("accept terms", { terms: " " })}
                                    </label>
                                    <Link
                                        id="link-terms"
                                        onClick={saveFormState}
                                        to="signup/terms"
                                        className="underline font-bold"
                                    >
                                        {t("terms & conditions")}
                                    </Link>
                                    {t("terms & policy", { terms: " ", privacy: " " })}
                                    <Link
                                        id="link-privacy"
                                        onClick={saveFormState}
                                        to="signup/privacy"
                                        className="underline font-bold"
                                    >
                                        {t("privacy policy")}
                                    </Link>
                                </div>
                                {askMarketing ? (
                                    <div className="mt-2">
                                        <input
                                            id="checkbox-marketing"
                                            ref={register({ required: false })}
                                            type="checkbox"
                                            name="marketing"
                                            onChange={() => triggerValidation("marketing")}
                                            className={errors.name ? "error" : ""}
                                        />
                                        <label htmlFor="checkbox-marketing" className="pl-2">
                                            {t("authorize marketing")}
                                        </label>
                                    </div>
                                ) : null}
                            </div>
                            <button
                                type="submit"
                                id="action-signup"
                                disabled={watch("terms") ? false : true}
                                className={
                                    "btn block mx-auto my-5 w-full text-lg " +
                                    (watch("terms") ? "btn-accent" : "btn-neutral-contrast-20")
                                }
                            >
                                {t("signup submit")}
                            </button>
                        </form>
                    </>
                )}
            </section>
        </section>
    );
};

export default SignUp;
